export type LoggingPreferences = {
  enableUniversalLogging: boolean;
  captureWindowErrors: boolean;
  capturePromiseRejections: boolean;
};

export type RecordViewPreferences = Record<string, string>;
export type TableColumnPreferences = Record<string, string[]>;

export type UserPreferences = {
  logging?: Partial<LoggingPreferences>;
  recordViews?: Record<string, string>;
  tableColumns?: Record<string, string[]>;
  [key: string]: unknown;
};

export const DEFAULT_LOGGING_PREFERENCES: LoggingPreferences = {
  enableUniversalLogging: true,
  captureWindowErrors: true,
  capturePromiseRejections: true,
};

/**
 * Resolve persisted logging preferences with defaults.
 */
export function resolveLoggingPreferences(source: unknown): LoggingPreferences {
  const raw = (source && typeof source === 'object' ? source : {}) as UserPreferences;
  const logging = raw.logging && typeof raw.logging === 'object' ? raw.logging : {};
  return {
    enableUniversalLogging:
      typeof logging.enableUniversalLogging === 'boolean'
        ? logging.enableUniversalLogging
        : DEFAULT_LOGGING_PREFERENCES.enableUniversalLogging,
    captureWindowErrors:
      typeof logging.captureWindowErrors === 'boolean'
        ? logging.captureWindowErrors
        : DEFAULT_LOGGING_PREFERENCES.captureWindowErrors,
    capturePromiseRejections:
      typeof logging.capturePromiseRejections === 'boolean'
        ? logging.capturePromiseRejections
        : DEFAULT_LOGGING_PREFERENCES.capturePromiseRejections,
  };
}

export function resolveRecordViewPreferences(source: unknown): RecordViewPreferences {
  const raw = (source && typeof source === 'object' ? source : {}) as UserPreferences;
  const recordViews = raw.recordViews;
  if (!recordViews || typeof recordViews !== 'object') {
    return {};
  }

  const entries = Object.entries(recordViews);
  if (!entries.length) {
    return {};
  }

  const cleaned: RecordViewPreferences = {};
  for (const [moduleKey, value] of entries) {
    if (typeof moduleKey !== 'string') {
      continue;
    }
    if (typeof value !== 'string') {
      continue;
    }
    const moduleId = moduleKey.trim();
    const viewId = value.trim();
    if (!moduleId || !viewId) {
      continue;
    }
    cleaned[moduleId] = viewId;
  }
  return cleaned;
}

export function resolveTableColumnPreferences(source: unknown): TableColumnPreferences {
  const raw = (source && typeof source === 'object' ? source : {}) as UserPreferences;
  const tableColumns = raw.tableColumns;
  if (!tableColumns || typeof tableColumns !== 'object') {
    return {};
  }

  const entries = Object.entries(tableColumns);
  if (!entries.length) {
    return {};
  }

  const cleaned: TableColumnPreferences = {};
  for (const [viewKey, value] of entries) {
    if (typeof viewKey !== 'string') {
      continue;
    }
    const normalizedViewKey = viewKey.trim();
    if (!normalizedViewKey) {
      continue;
    }
    if (!Array.isArray(value)) {
      continue;
    }
    const seen = new Set<string>();
    const next: string[] = [];
    for (const item of value) {
      if (typeof item !== 'string') continue;
      const columnId = item.trim();
      if (!columnId) continue;
      if (seen.has(columnId)) continue;
      seen.add(columnId);
      next.push(columnId);
    }
    cleaned[normalizedViewKey] = next;
  }
  return cleaned;
}

/**
 * Merge logging preference updates back into a broader preference object.
 */
export function mergeLoggingPreferenceUpdate(
  base: UserPreferences | undefined,
  updates: Partial<LoggingPreferences>
): UserPreferences {
  const current = resolveLoggingPreferences(base);
  const nextLogging: LoggingPreferences = {
    ...current,
    ...updates,
  };
  return {
    ...(base || {}),
    logging: nextLogging,
  };
}

export function mergeRecordViewPreferenceUpdate(
  base: UserPreferences | undefined,
  moduleKey: string,
  viewId: string | null | undefined
): UserPreferences {
  const normalizedModule = moduleKey.trim();
  if (!normalizedModule) {
    return { ...(base || {}) };
  }

  const current = resolveRecordViewPreferences(base);
  const next: RecordViewPreferences = { ...current };
  if (!viewId) {
    delete next[normalizedModule];
  } else {
    const normalizedView = viewId.trim();
    if (normalizedView) {
      next[normalizedModule] = normalizedView;
    } else {
      delete next[normalizedModule];
    }
  }

  if (!Object.keys(next).length) {
    const clone = { ...(base || {}) } as UserPreferences;
    delete clone.recordViews;
    return clone;
  }

  return {
    ...(base || {}),
    recordViews: next,
  };
}

export function mergeTableColumnPreferenceUpdate(
  base: UserPreferences | undefined,
  viewKey: string,
  columnIds: string[] | null | undefined,
): UserPreferences {
  const normalizedViewKey = viewKey.trim();
  if (!normalizedViewKey) {
    return { ...(base || {}) };
  }

  const current = resolveTableColumnPreferences(base);
  const next: TableColumnPreferences = { ...current };

  const incoming = Array.isArray(columnIds) ? columnIds : [];
  const seen = new Set<string>();
  const cleaned: string[] = [];
  for (const raw of incoming) {
    if (typeof raw !== 'string') continue;
    const columnId = raw.trim();
    if (!columnId) continue;
    if (seen.has(columnId)) continue;
    seen.add(columnId);
    cleaned.push(columnId);
  }

  if (!Array.isArray(columnIds)) {
    delete next[normalizedViewKey];
  } else {
    next[normalizedViewKey] = cleaned;
  }

  if (!Object.keys(next).length) {
    const clone = { ...(base || {}) } as UserPreferences;
    delete clone.tableColumns;
    return clone;
  }

  return {
    ...(base || {}),
    tableColumns: next,
  };
}
